import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";

import { useAuth } from "../contexts/authContext";
import apiService from "../services/apiService";

/**
 * Component to display confirmation popup to handle token expiration and
 * provides options to stay logged in or logout.
 */
const TokenExpirationModal = () => {
  const { user, token, refreshToken, login, logout } = useAuth();
  const [showExpirationModal, setShowExpirationModal] = useState(false);

  const closeModal = () => setShowExpirationModal(false);

  const getTokenExpirationTime = (token) => {
    try {
      const { exp } = JSON.parse(atob(token.split(".")[1]));
      return exp * 1000; // Convert to milliseconds
    } catch (error) {
      return null;
    }
  };

  const refreshAccessToken = async () => {
    try {
      const response = await apiService.post("/auth/refresh-token", {
        refreshToken,
      });
      login({
        user, // Keep the user data as it is
        token: response.token,
        refreshToken: response.refreshToken,
      });
    } catch (error) {
      console.error("Error refreshing token:", error);
      logout();
    } finally {
      closeModal();
    }
  };

  useEffect(() => {
    if (token) {
      const expirationTime = getTokenExpirationTime(token);
      if (expirationTime === null) {
        // Handle invalid token or missing expiration time
        logout();
      } else {
        const remainingTime = expirationTime - Date.now();

        // Show the modal 10 seconds before expiration
        if (remainingTime > 10000) {
          const timer = setTimeout(
            () => setShowExpirationModal(true),
            remainingTime - 10000
          );

          return () => clearTimeout(timer); // Cleanup timer on unmount
        } else {
          setShowExpirationModal(true); // Show modal immediately if token is already expired
        }
      }
    }
  }, [token, logout]);

  return (
    <Modal
      show={showExpirationModal}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
      contentClassName="p-4 border-0"
    >
      <Modal.Body>
        <h5>Session Timeout!</h5>
        <p className="mb-1 text-muted">
          Your session is about to expire! Click below to stay signed in and
          continue your session."
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="danger"
          className="btn-main"
          onClick={() => {
            logout();
            closeModal();
          }}
        >
          Logout
        </Button>
        <Button
          variant="primary"
          className="btn-main"
          onClick={refreshAccessToken}
        >
          Stay Signed In
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TokenExpirationModal;
